/** Monday-Sunday training-block analytics: % by zone and week-to-week volume. */

export const parseMdy = (value) => {
  if (!value) return null;
  const parts = String(value).trim().split('/');
  if (parts.length !== 3) return null;
  const [m, d, y] = parts.map((p) => parseInt(p, 10));
  if (!m || !d || !y || m > 12 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date;
};

export const formatMdy = (date) => {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${m}/${d}/${date.getFullYear()}`;
};

export const addDays = (date, days) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
};

export const mondayOf = (date) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = next.getDay();
  next.setDate(next.getDate() + (day === 0 ? -6 : 1 - day));
  return next;
};

export const weekStartForDate = (mdy) => {
  const date = parseMdy(mdy);
  if (!date) return null;
  return formatMdy(mondayOf(date));
};

export const zoneIncludedInPct = (zone) => zone?.includeInZonePct !== false;

// Keep in sync with backend/utils/featureFlags.js DEFAULT_ZONE_COLORS.
export const DEFAULT_ZONE_CHART_COLORS = [
  '#2563eb',
  '#0891b2',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#db2777',
  '#4f46e5'
];

export const resolveZoneColor = (zone, index = 0) => {
  const raw = String(zone?.color || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw;
  return DEFAULT_ZONE_CHART_COLORS[index % DEFAULT_ZONE_CHART_COLORS.length];
};

const zonesTotal = (zones) =>
  Object.values(zones || {}).reduce((sum, value) => sum + (parseInt(value, 10) || 0), 0);

/** Builder sessions ({ minutes, zones }) and API entries ({ plannedMinutes, plannedZones }). */
export const toSessionLike = (item) => {
  if (!item) return { minutes: 0, zones: {} };
  const zones = item.zones || item.plannedZones || {};
  const explicit = parseInt(item.minutes ?? item.plannedMinutes, 10);
  const zoneSum = zonesTotal(zones);
  const minutes = !Number.isNaN(explicit) && explicit > 0 ? explicit : zoneSum;
  return { minutes, zones };
};

const roundPcts = (items) => {
  const total = items.reduce((sum, item) => sum + item.minutes, 0);
  if (total <= 0) return items.map((item) => ({ ...item, pct: 0 }));
  const rounded = items.map((item) => ({
    ...item,
    pct: Math.round((item.minutes / total) * 100)
  }));
  const drift = 100 - rounded.reduce((sum, item) => sum + item.pct, 0);
  if (drift !== 0 && rounded.length > 0) {
    let largest = rounded[0];
    for (const item of rounded) {
      if (item.minutes > largest.minutes) largest = item;
    }
    largest.pct += drift;
  }
  return rounded;
};

export const summarizeSessions = (sessions, zoneDefinitions = []) => {
  const defs = Array.isArray(zoneDefinitions) ? zoneDefinitions : [];
  let totalMinutes = 0;
  let sessionCount = 0;
  const zoneMinutes = {};

  for (const raw of sessions || []) {
    const { minutes, zones } = toSessionLike(raw);
    if (minutes <= 0) continue;
    totalMinutes += minutes;
    sessionCount += 1;
    Object.entries(zones || {}).forEach(([key, value]) => {
      const zMins = parseInt(value, 10);
      if (zMins > 0) zoneMinutes[key] = (zoneMinutes[key] || 0) + zMins;
    });
  }

  const zonedTotal = Object.values(zoneMinutes).reduce((sum, value) => sum + value, 0);
  const unzoned = Math.max(0, totalMinutes - zonedTotal);

  const includedZoneMinutes = {};
  const excludedZoneMinutes = {};
  let includedTotal = 0;
  let excludedMinutes = 0;

  for (const [key, mins] of Object.entries(zoneMinutes)) {
    const def = defs.find((zone) => zone.key === key);
    const included = def ? zoneIncludedInPct(def) : true;
    if (included) {
      includedZoneMinutes[key] = mins;
      includedTotal += mins;
    } else {
      excludedZoneMinutes[key] = mins;
      excludedMinutes += mins;
    }
  }

  const knownKeys = new Set(defs.map((zone) => zone.key));
  const zonePcts = roundPcts([
    ...defs
      .filter((zone) => includedZoneMinutes[zone.key])
      .map((zone, index) => ({
        key: zone.key,
        label: zone.label,
        minutes: includedZoneMinutes[zone.key],
        color: resolveZoneColor(zone, index)
      })),
    ...Object.entries(includedZoneMinutes)
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, minutes], index) => ({
        key,
        label: key,
        minutes,
        color: resolveZoneColor(null, defs.length + index)
      }))
  ]);

  const excludedZones = [
    ...defs
      .filter((zone) => excludedZoneMinutes[zone.key])
      .map((zone) => ({
        key: zone.key,
        label: zone.label,
        minutes: excludedZoneMinutes[zone.key]
      })),
    ...Object.entries(excludedZoneMinutes)
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, minutes]) => ({ key, label: key, minutes }))
  ];

  return {
    totalMinutes,
    sessionCount,
    zoneMinutes,
    includedTotal,
    excludedMinutes,
    excludedZones,
    unzoned,
    zonePcts
  };
};

export const volumeDelta = (currentMinutes, previousMinutes) => {
  const current = Number(currentMinutes) || 0;
  const previous = Number(previousMinutes);
  if (!Number.isFinite(previous) || previous <= 0) {
    return { minutes: null, pct: null };
  }
  const minutes = current - previous;
  return { minutes, pct: Math.round((minutes / previous) * 100) };
};

export const formatVolumeDelta = (delta) => {
  if (!delta || delta.minutes == null) return null;
  if (delta.minutes === 0) return 'same volume as last week';
  const sign = delta.minutes > 0 ? '+' : '';
  const pctPart = delta.pct != null ? ` (${sign}${delta.pct}%)` : '';
  return `${sign}${delta.minutes}' vs last week${pctPart}`;
};

/**
 * One summary per Monday-starting week that has planned sessions.
 * Delta is vs the previous calendar week (missing week = 0 volume).
 */
export const groupPlanEntriesByWeek = (entries, zoneDefinitions) => {
  const byWeek = new Map();
  for (const entry of entries || []) {
    const weekStart = weekStartForDate(entry.date);
    if (!weekStart) continue;
    if (!byWeek.has(weekStart)) byWeek.set(weekStart, []);
    byWeek.get(weekStart).push(entry);
  }

  const weekStarts = [...byWeek.keys()].sort((a, b) => parseMdy(a) - parseMdy(b));
  return weekStarts.map((weekStart) => {
    const monday = parseMdy(weekStart);
    const weekEnd = formatMdy(addDays(monday, 6));
    const prevKey = formatMdy(addDays(monday, -7));
    const totals = summarizeSessions(byWeek.get(weekStart), zoneDefinitions);
    const prevTotals = byWeek.has(prevKey)
      ? summarizeSessions(byWeek.get(prevKey), zoneDefinitions)
      : { totalMinutes: 0 };
    return {
      weekStart,
      weekEnd,
      totals,
      delta: volumeDelta(totals.totalMinutes, prevTotals.totalMinutes)
    };
  });
};

export const contiguousBlockWeeks = (weekSummaries, focusWeekStart) => {
  const weeks = Array.isArray(weekSummaries) ? weekSummaries : [];
  if (weeks.length === 0) return [];
  const index = weeks.findIndex((week) => week.weekStart === focusWeekStart);
  if (index < 0) return [];

  const isAdjacent = (a, b) => {
    const mondayA = parseMdy(a.weekStart);
    const mondayB = parseMdy(b.weekStart);
    if (!mondayA || !mondayB) return false;
    const diffDays = Math.abs((mondayB - mondayA) / (24 * 60 * 60 * 1000));
    return diffDays === 7;
  };

  let start = index;
  let end = index;
  while (start > 0 && isAdjacent(weeks[start - 1], weeks[start])) start -= 1;
  while (end < weeks.length - 1 && isAdjacent(weeks[end], weeks[end + 1])) end += 1;
  return weeks.slice(start, end + 1);
};
